import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, Users, CalendarCheck, Loader2 } from "lucide-react";
import apiClient from "@/lib/api";

/**
 * Doctor-only statistics dashboard: 14-day registration trend, status
 * breakdown, and the most frequent diagnoses among completed visits.
 * Uses recharts (already a project dependency, previously unused).
 */
const StatsDashboard = ({ open, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiClient.get("/stats")
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [open]);

  const dayLabel = (d) => d?.slice(5); // "2026-07-24" -> "07-24"

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <TrendingUp className="w-5 h-5 text-[#5B3A7D] dark:text-violet-400" />
            إحصائيات العيادة
          </DialogTitle>
          <DialogDescription>نظرة سريعة على نشاط العيادة خلال آخر أسبوعين</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-400 dark:text-ink-300">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}

        {!loading && stats && (
          <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-slate-200 dark:border-ink-700 p-4 text-center">
                <Users className="w-5 h-5 mx-auto text-[#5B3A7D] dark:text-violet-400 mb-1" />
                <p className="text-2xl font-bold text-[#1F2937] dark:text-ink-50">{stats.total_patients}</p>
                <p className="text-xs text-slate-500 dark:text-ink-300">إجمالي المرضى</p>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-ink-700 p-4 text-center">
                <CalendarCheck className="w-5 h-5 mx-auto text-emerald-600 dark:text-emerald-400 mb-1" />
                <p className="text-2xl font-bold text-[#1F2937] dark:text-ink-50">{stats.today_patients}</p>
                <p className="text-xs text-slate-500 dark:text-ink-300">مرضى اليوم</p>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-ink-700 p-4 text-center">
                <TrendingUp className="w-5 h-5 mx-auto text-amber-600 dark:text-amber-400 mb-1" />
                <p className="text-2xl font-bold text-[#1F2937] dark:text-ink-50">{stats.completed_patients}</p>
                <p className="text-xs text-slate-500 dark:text-ink-300">فحوصات مكتملة</p>
              </div>
            </div>

            {/* 14-day trend */}
            <div className="rounded-xl border border-slate-200 dark:border-ink-700 p-4">
              <p className="text-sm font-bold text-slate-500 dark:text-ink-300 mb-3">تسجيلات المرضى — آخر 14 يوماً</p>
              <div className="h-52" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.daily_counts} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-slate-200 dark:text-ink-700" />
                    <XAxis dataKey="day" tickFormatter={dayLabel} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <Tooltip
                      labelFormatter={(d) => `اليوم: ${d}`}
                      formatter={(v) => [`${v} مريض`, ""]}
                      contentStyle={{ direction: "rtl", borderRadius: 8 }}
                    />
                    <Line type="monotone" dataKey="count" stroke="#5B3A7D" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Top diagnoses */}
            {stats.top_diagnoses?.length > 0 && (
              <div className="rounded-xl border border-slate-200 dark:border-ink-700 p-4">
                <p className="text-sm font-bold text-slate-500 dark:text-ink-300 mb-3">أكثر التشخيصات تكراراً</p>
                <div className="h-48" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.top_diagnoses} layout="vertical" margin={{ top: 0, right: 10, left: 10, bottom: 0 }}>
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis
                        type="category" dataKey="diagnosis" width={120}
                        tick={{ fontSize: 11 }} stroke="#94a3b8"
                        tickFormatter={(d) => (d.length > 18 ? d.slice(0, 18) + "…" : d)}
                      />
                      <Tooltip formatter={(v) => [`${v} حالة`, ""]} contentStyle={{ direction: "rtl", borderRadius: 8 }} />
                      <Bar dataKey="count" fill="#5B3A7D" radius={[0, 6, 6, 0]} barSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && !stats && (
          <p className="text-center text-slate-400 dark:text-ink-300 py-10">تعذّر تحميل الإحصائيات</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StatsDashboard;
